import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { TaskDetail, ThreadInteraction, ThreadMessage, ThreadView, MissionContextData } from "../types";
import { useAgenticSubscription } from "@/hooks/useAgenticSubscription";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import { missionControlKeys } from "./keys";
import {
  mergeThreadViews,
  patchThreadMessage,
  patchThreadStep,
  hydrateArtifactCardMessage,
} from "./patches";
import { threadArtifactCardNeedsFetch, threadMessageJsonContent } from "@/services/api/agentic-v2/threadArtifactContent";

export interface UseTaskThreadOptions {
  taskId: string;
  enabled?: boolean;
}

export interface UseTaskThreadResult {
  task: TaskDetail | null;
  thread: ThreadView | null;
  missionContext: MissionContextData | null;
  loading: boolean;
  refreshing: boolean;
  error: Error | null;
  isAgentWorking: boolean;
  activityLabel: string | null;
  isConnected: boolean;
  isWindowLive: boolean;
  setTask: Dispatch<SetStateAction<TaskDetail | null>>;
  setThread: Dispatch<SetStateAction<ThreadView | null>>;
  setError: Dispatch<SetStateAction<Error | null>>;
  /** Refresh task + thread, preferring an incremental fetch when a cursor is cached. */
  silentRefresh: () => Promise<void>;
  /** Full thread reload — discards incremental cursor and replaces the cached thread. */
  reloadThread: () => Promise<void>;
}

/** True when the cached thread has an agent interaction still in ``running`` status. */
function hasRunningInteraction(thread: ThreadView | null | undefined): boolean {
  return thread?.interactions?.some((row) => row.status === "running") ?? false;
}

/** True when an ``artifact_card`` WS row still needs API hydration to render. */
function threadMessageNeedsHydration(message: ThreadMessage): boolean {
  if (message.content_type !== "artifact_card") return false;
  const content = threadMessageJsonContent(message.content);
  if (!content) return false;
  return threadArtifactCardNeedsFetch(content);
}

/** Task detail + incremental thread via TanStack Query and agentic WS patches. */
export function useTaskThread({
  taskId,
  enabled: enabledOption = true,
}: UseTaskThreadOptions): UseTaskThreadResult {
  const queryClient = useQueryClient();
  const isWindowLive = useWindowLive();
  const queryEnabled = enabledOption && isWindowLive;

  const [taskOverride, setTaskOverride] = useState<TaskDetail | null>(null);
  const [threadOverride, setThreadOverride] = useState<ThreadView | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isAgentWorking, setIsAgentWorking] = useState(false);
  const [activityLabel, setActivityLabel] = useState<string | null>(null);

  const taskQuery = useQuery({
    queryKey: missionControlKeys.task(taskId),
    enabled: queryEnabled,
    refetchOnWindowFocus: queryEnabled,
    queryFn: () => AgenticV2API.getTask(taskId),
  });

  const threadQuery = useQuery({
    queryKey: missionControlKeys.taskThread(taskId),
    enabled: queryEnabled,
    // Thread is merged from WS deltas; avoid focus refetch racing silentRefresh.
    refetchOnWindowFocus: false,
    queryFn: () => AgenticV2API.getTaskThread(taskId),
  });

  const missionId = taskQuery.data?.mission?.id ?? taskOverride?.mission?.id;
  const missionContextQuery = useQuery({
    queryKey: missionControlKeys.missionContext(missionId ?? ""),
    enabled: queryEnabled && Boolean(missionId),
    queryFn: async () => {
      const mission = await AgenticV2API.getMission(missionId!);
      return mission.context ?? null;
    },
  });

  useEffect(() => {
    setTaskOverride(null);
    setThreadOverride(null);
    setError(null);
    setIsAgentWorking(false);
    setActivityLabel(null);
  }, [taskId]);

  useEffect(() => {
    if (taskQuery.error) {
      setError(
        taskQuery.error instanceof Error
          ? taskQuery.error
          : new Error(String(taskQuery.error)),
      );
    }
  }, [taskQuery.error]);

  useEffect(() => {
    if (!threadQuery.data) return;
    if (hasRunningInteraction(threadQuery.data)) {
      setIsAgentWorking(true);
    } else if (!threadOverride) {
      setIsAgentWorking(false);
      setActivityLabel(null);
    }
  }, [threadQuery.data, threadOverride]);

  const task = taskOverride ?? taskQuery.data ?? null;
  const thread = threadOverride ?? threadQuery.data ?? null;
  const missionContext = missionContextQuery.data ?? null;

  const loading =
    (taskQuery.isLoading || threadQuery.isLoading) && task === null;
  const refreshing =
    (taskQuery.isFetching || threadQuery.isFetching) && !loading;

  const silentRefresh = useCallback(async () => {
    const cachedThread = queryClient.getQueryData<ThreadView>(
      missionControlKeys.taskThread(taskId),
    );
    const cursor = cachedThread?.cursor ?? thread?.cursor ?? null;

    setThreadOverride(null);

    // Prefer incremental thread fetch when we have a cursor from the last load.
    if (cursor && task) {
      try {
        const [taskData, delta] = await Promise.all([
          AgenticV2API.getTask(taskId),
          AgenticV2API.getTaskThread(taskId, { since: cursor }),
        ]);
        if (taskData._id !== taskId) return;
        queryClient.setQueryData(missionControlKeys.task(taskId), taskData);
        setTaskOverride(taskData);
        queryClient.setQueryData<ThreadView>(
          missionControlKeys.taskThread(taskId),
          (prev) => {
            const base = prev ?? cachedThread;
            if (!base || !delta.is_delta) return delta;
            return mergeThreadViews(base, delta);
          },
        );
        if (delta.interactions?.some((row) => row.status === "running")) {
          setIsAgentWorking(true);
        }
        return;
      } catch {
        // Fall through to full reload.
      }
    }

    try {
      const [taskData, threadData] = await Promise.all([
        AgenticV2API.getTask(taskId),
        AgenticV2API.getTaskThread(taskId),
      ]);
      if (taskData._id !== taskId) return;
      queryClient.setQueryData(missionControlKeys.task(taskId), taskData);
      queryClient.setQueryData(missionControlKeys.taskThread(taskId), threadData);
      setTaskOverride(taskData);
      setThreadOverride(threadData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, [queryClient, task, taskId, thread?.cursor]);

  const reloadThread = useCallback(async () => {
    const threadData = await AgenticV2API.getTaskThread(taskId);
    queryClient.setQueryData(missionControlKeys.taskThread(taskId), threadData);
    setThreadOverride(threadData);
  }, [queryClient, taskId]);

  const debouncedSilentRefresh = useMemo(
    () => debounce(() => { void silentRefresh(); }, 500),
    [silentRefresh],
  );

  useEffect(() => () => debouncedSilentRefresh.cancel(), [debouncedSilentRefresh]);

  const wsSubscription = useMemo(
    () => ({
      onTaskStatusChanged: (payload: { task_id: string; new_status: string }) => {
        if (payload.task_id !== taskId) return;
        queryClient.setQueryData<TaskDetail>(
          missionControlKeys.task(taskId),
          (prev) => (prev ? { ...prev, status: payload.new_status } : prev),
        );
        setTaskOverride((prev) =>
          prev ? { ...prev, status: payload.new_status } : prev,
        );
        debouncedSilentRefresh();
      },
      onTaskNeedsAttention: (payload: { task_id: string }) => {
        if (payload.task_id === taskId) debouncedSilentRefresh();
      },
      onInteractionStarted: (payload: { task_id: string | null }) => {
        if (payload.task_id !== taskId) return;
        setIsAgentWorking(true);
        setActivityLabel("Thinking…");
        debouncedSilentRefresh();
      },
      onInteractionProgress: (payload: {
        task_id: string | null;
        interaction_id: string;
        label: string | null;
        thread_dirty: boolean;
        step_index: number | null;
        step?: ThreadInteraction["steps"][number] | null;
        message?: ThreadMessage | null;
      }) => {
        if (payload.task_id !== taskId) return;
        setIsAgentWorking(true);
        if (payload.label) setActivityLabel(payload.label);
        if (payload.message) {
          patchThreadMessage(queryClient, taskId, payload.message);
          setThreadOverride(null);
          if (threadMessageNeedsHydration(payload.message)) {
            void hydrateArtifactCardMessage(
              queryClient,
              taskId,
              payload.message,
            );
          }
          return;
        }
        if (
          payload.step &&
          payload.step_index != null &&
          payload.interaction_id
        ) {
          patchThreadStep(queryClient, taskId, {
            interaction_id: payload.interaction_id,
            step_index: payload.step_index,
            step: payload.step,
          });
          setThreadOverride(null);
          if (payload.thread_dirty) debouncedSilentRefresh();
          return;
        }
        if (payload.thread_dirty) debouncedSilentRefresh();
      },
      onInteractionCompleted: (payload: { task_id: string | null }) => {
        if (payload.task_id !== taskId) return;
        setIsAgentWorking(false);
        setActivityLabel(null);
        debouncedSilentRefresh();
      },
    }),
    [debouncedSilentRefresh, queryClient, taskId],
  );

  const { isConnected, isWindowLive: windowLive } = useAgenticSubscription(
    wsSubscription,
    { enabled: queryEnabled },
  );

  useEffect(() => {
    if (!isAgentWorking) return;
    if (isConnected && windowLive) return;
    const interval = setInterval(() => {
      void silentRefresh();
    }, 8000);
    return () => clearInterval(interval);
  }, [isAgentWorking, isConnected, silentRefresh, windowLive]);

  return {
    task,
    thread,
    missionContext,
    loading,
    refreshing,
    error,
    isAgentWorking,
    activityLabel,
    isConnected,
    isWindowLive: windowLive,
    setTask: setTaskOverride,
    setThread: setThreadOverride,
    setError,
    silentRefresh,
    reloadThread,
  };
}
